import React, { useState, useEffect } from 'react';
import { pollsService } from '../../services/firestoreService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './LivePolls.css';

const LivePolls = ({ sessionId, studentId }) => {
  const [polls, setPolls] = useState([]);
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [pollResults, setPollResults] = useState(null);
  const [studentResponse, setStudentResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Subscribe to polls
    const unsubscribe = pollsService.subscribeToPolls(sessionId, (pollsList) => {
      setPolls(pollsList);
      if (pollsList.length > 0 && !selectedPoll) {
        setSelectedPoll(pollsList[0]);
      }
    });

    return () => unsubscribe();
  }, [sessionId, selectedPoll]);

  useEffect(() => {
    if (selectedPoll) {
      // Subscribe to poll results
      const unsubscribe = pollsService.subscribeToPollResults(sessionId, selectedPoll.id, (results) => {
        setPollResults(results);
      });

      // Get student's response
      pollsService.getPollResults(sessionId, selectedPoll.id).then(async (results) => {
        if (results) {
          setPollResults(results);
          // Find student's response
          try {
            const responsesRef = collection(db, 'sessions', sessionId, 'polls', selectedPoll.id, 'responses');
            const q = query(responsesRef, where('studentId', '==', studentId));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
              setStudentResponse(querySnapshot.docs[0].data().optionIndex);
            }
          } catch (error) {
            console.error('Error getting student response:', error);
          }
        }
      });

      return () => unsubscribe();
    }
  }, [selectedPoll, sessionId, studentId]);

  const handleSubmitResponse = async (optionIndex) => {
    if (!selectedPoll) return;
    setLoading(true);
    try {
      await pollsService.submitPollResponse(sessionId, selectedPoll.id, studentId, optionIndex);
      setStudentResponse(optionIndex);
    } catch (error) {
      console.error('Error submitting poll response:', error);
      alert('Error submitting response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (polls.length === 0) {
    return (
      <div className="live-polls-container">
        <div className="no-polls">
          <p>📊 No active polls at the moment.</p>
          <p className="hint">Your instructor will create polls during the class!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="live-polls-container">
      <h2>📊 Live Polls & Quizzes</h2>
      
      {polls.length > 1 && (
        <div className="polls-list">
          {polls.map((poll) => (
            <button
              key={poll.id}
              className={`poll-tab ${selectedPoll?.id === poll.id ? 'active' : ''}`}
              onClick={() => setSelectedPoll(poll)}
            >
              {poll.question}
            </button>
          ))}
        </div>
      )}

      {selectedPoll && (
        <div className="poll-card">
          <h3>{selectedPoll.question}</h3>
          {selectedPoll.description && (
            <p className="poll-description">{selectedPoll.description}</p>
          )}

          {studentResponse === null ? (
            <div className="poll-options">
              {selectedPoll.options.map((option, index) => (
                <button
                  key={index}
                  className="poll-option"
                  onClick={() => handleSubmitResponse(index)}
                  disabled={loading}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : (
            <div className="poll-results">
              <p className="response-confirmation">✅ You selected: <strong>{selectedPoll.options[studentResponse]}</strong></p>
              
              {pollResults && (
                <div className="results-display">
                  <h4>Results:</h4>
                  {selectedPoll.options.map((option, index) => {
                    const result = pollResults.results[index] || { count: 0, percentage: 0 };
                    return (
                      <div key={index} className="result-item">
                        <div className="result-header">
                          <span className="option-text">{option}</span>
                          <span className="result-stats">
                            {result.count} votes ({result.percentage}%)
                          </span>
                        </div>
                        <div className="result-bar-container">
                          <div
                            className={`result-bar ${studentResponse === index ? 'your-choice' : ''}`}
                            style={{ width: `${result.percentage}%` }}
                          >
                            {result.percentage > 5 && `${result.percentage}%`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <p className="total-responses">Total: {pollResults.totalResponses} responses</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LivePolls;

